/** Preserve a practice runner while replacing the bounded window of course around them. */
import { Sim } from "./runner";
import type { SimState } from "./state";
import { type CourseData, copyInputFrame } from "./types";
import type { World } from "./world";

const SAME_POSITION = 1e-7;

function near(a: number, b: number): boolean {
  return Math.abs(a - b) <= SAME_POSITION;
}

/** Translate a live simulation after consuming its tick events, without restarting its movement. */
export function rebasePracticeSim(sim: Sim, course: CourseData, offset: number): Sim {
  const next = new Sim(course, sim.tuning);
  const before = sim.world;
  const after = next.world;
  const solids = new Int32Array(before.solidCount).fill(-1);
  const bars = new Int32Array(before.barCount).fill(-1);
  const ladders = new Int32Array(before.ladderCount).fill(-1);
  for (let i = 0; i < before.solidCount; i++) {
    for (let j = 0; j < after.solidCount; j++) {
      if (
        before.solidClass[i] === after.solidClass[j] &&
        near(before.solidX0[i]! - offset, after.solidX0[j]!) &&
        near(before.solidX1[i]! - offset, after.solidX1[j]!) &&
        near(before.solidY0[i]!, after.solidY0[j]!) &&
        near(before.solidY1[i]!, after.solidY1[j]!)
      ) {
        solids[i] = j;
        break;
      }
    }
  }
  for (let i = 0; i < before.ladderCount; i++) {
    for (let j = 0; j < after.ladderCount; j++) {
      if (
        near(before.ladderX[i]! - offset, after.ladderX[j]!) &&
        near(before.ladderY0[i]!, after.ladderY0[j]!) &&
        before.ladderRungs[i] === after.ladderRungs[j] &&
        near(before.ladderSpacing[i]!, after.ladderSpacing[j]!)
      ) {
        ladders[i] = j;
        break;
      }
    }
  }
  for (let i = 0; i < before.barCount; i++) {
    const ladder = before.barLadder[i]!;
    for (let j = 0; j < after.barCount; j++) {
      if (
        near(before.barX[i]! - offset, after.barX[j]!) &&
        near(before.barY[i]!, after.barY[j]!) &&
        (ladder < 0
          ? after.barLadder[j] === -1
          : ladders[ladder] === after.barLadder[j] && before.barRung[i] === after.barRung[j])
      ) {
        bars[i] = j;
        break;
      }
    }
  }
  next.state.copyFrom(sim.state);
  next.prev.copyFrom(sim.prev);
  for (const state of [next.state, next.prev]) {
    state.x -= offset;
    state.shoulderX -= offset;
    state.handX -= offset;
    state.ledgeGrabX -= offset;
    state.barId = mapped(bars, state.barId);
    state.lastBarId = mapped(bars, state.lastBarId);
    state.assistBar = mapped(bars, state.assistBar);
    state.ladderId = mapped(ladders, state.ladderId);
    state.plantBox = mapped(solids, state.plantBox);
    state.vaultArmBox = mapped(solids, state.vaultArmBox);
    state.slideBoostPipe = mapped(solids, state.slideBoostPipe);
    if (state.slideBoostPipe < 0) state.slideBoostReady = 0;
    state.ledgeSolid = mapped(solids, state.ledgeSolid);
    state.lastLedge = mapped(solids, state.lastLedge);
    rebaseProgress(state, before, after, offset);
  }
  copyInputFrame(next.input, sim.input);
  return next;
}

function mapped(indices: Int32Array, index: number): number {
  return index < 0 ? -1 : (indices[index] ?? -1);
}

function rebaseProgress(state: SimState, before: World, after: World, offset: number): void {
  // A checkpoint remains the last one passed even if the runner turns back. If the old checkpoint
  // left the retained window, use its oldest safe point, never a point ahead of the old checkpoint.
  const checkpointX = (before.respawnX[state.respawnIndex] ?? before.startX) - offset;
  let respawn = 0;
  while (respawn + 1 < after.respawnCount && after.respawnX[respawn + 1]! <= checkpointX + SAME_POSITION)
    respawn++;
  state.respawnIndex = respawn;
  const nextMark = before.markX[state.markIndex];
  let mark = 0;
  if (nextMark === undefined) {
    const lastPassedX = before.markX[state.markIndex - 1];
    const through = Math.max(state.x, lastPassedX === undefined ? state.x : lastPassedX - offset);
    while (mark < after.markCount && after.markX[mark]! <= through + SAME_POSITION) mark++;
  } else {
    while (mark < after.markCount && after.markX[mark]! < nextMark - offset - SAME_POSITION) mark++;
  }
  state.markIndex = mark;
}
